// Ящик «Помощь и поддержка» (T-HELP-4) — HelpDrawer кита монтируется напрямую (это статически
// импортированный шаблон кита, не отдельный продукт, поэтому без host-DOM трюка useProfileDrawer:
// тот же приём, что ModalOverlay для Modal кита). Хук держит открыт/вид/тосты/счётчик непрочитанного.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { makeSupportApi } from "../api/support";
import { sharedEventBus } from "../osnovaContext";
// Живого события о поддержке в потоке Оболочки (src/live) нет — счётчик обновляем по входу
// и раз в минуту; открытие/действия внутри самого HelpDrawer обновляют его сами через onUnread.
const HELP_UNREAD_POLL_MS = 60_000;
export function useHelpDrawer(vp, loggedIn) {
    const api = useMemo(() => makeSupportApi(), []);
    const [mounted, setMounted] = useState(false);
    const [visible, setVisible] = useState(false);
    const [view, setView] = useState("msg");
    // prefill — чем открыть форму обращения (kind/subject/module), когда панель зовут из продукта
    // (см. HelpOpenPayload в osnovaContext.ts), а не из пункта меню «Помощь» шапки.
    const [prefill, setPrefill] = useState(undefined);
    const [helpUnread, setHelpUnread] = useState(0);
    const [toasts, setToasts] = useState([]);
    const toastSeq = useRef(0);
    const toastTimers = useRef(new Map());
    const openHelp = useCallback((id, pf) => {
        setView(id);
        setPrefill(pf);
        setMounted(true);
        setVisible(true);
    }, []);
    const closeHelp = useCallback(() => setVisible(false), []);
    const onClosed = useCallback(() => setMounted(false), []);
    // Единая точка открытия панели помощи с предзаполнением — продукт шлёт platform:open-help
    // (тот же приём шины, что platform:open-profile у useProfileDrawer.ts), Маркетплейс — образец
    // эмиттера (products/marketplace/front/src/entry.tsx, onSupport).
    useEffect(() => sharedEventBus.on("platform:open-help", (data) => {
        const payload = data;
        const target = payload?.view && ["msg", "tickets", "new"].includes(payload.view) ? payload.view : "msg";
        openHelp(target, payload?.prefill);
    }), [openHelp]);
    const onToast = useCallback((tst) => {
        const id = ++toastSeq.current;
        const tone = tst.tone === "success" || tst.tone === "warn" || tst.tone === "danger" ? tst.tone : "info";
        setToasts((cur) => [...cur, { id, tone, title: tst.title, body: tst.body }]);
        const timer = window.setTimeout(() => {
            toastTimers.current.delete(id);
            setToasts((cur) => cur.filter((x) => x.id !== id));
        }, 5000);
        toastTimers.current.set(id, timer);
    }, []);
    const dismissToast = useCallback((id) => {
        const timer = toastTimers.current.get(id);
        if (timer !== undefined) {
            window.clearTimeout(timer);
            toastTimers.current.delete(id);
        }
        setToasts((cur) => cur.filter((x) => x.id !== id));
    }, []);
    // размонтирование хука (выход/смена страницы) — снять все ждущие таймеры тостов.
    useEffect(() => {
        const timers = toastTimers.current;
        return () => { timers.forEach((timer) => window.clearTimeout(timer)); timers.clear(); };
    }, []);
    // непрочитанное — сразу после входа и раз в минуту; readTicket внутри открытого ящика бьёт
    // сюда же через onUnread (проп HelpDrawer, см. рендер в App.tsx).
    useEffect(() => {
        if (!loggedIn) {
            setHelpUnread(0);
            return;
        }
        let dead = false;
        const refresh = () => {
            api.getSupportUnread?.().then((d) => { if (!dead)
                setHelpUnread(d.count); }).catch(() => { });
        };
        refresh();
        const timer = window.setInterval(refresh, HELP_UNREAD_POLL_MS);
        return () => { dead = true; window.clearInterval(timer); };
    }, [loggedIn, api]);
    const helpVp = vp === "mobile" ? "mobile" : "desktop";
    return {
        api, mounted, visible, view, prefill, helpVp, helpUnread, setHelpUnread,
        openHelp, closeHelp, onClosed, toasts, onToast, dismissToast,
    };
}
